import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';

import { ReportFieldContainer, ReportSchema } from '..';

import ErrorIconPath from '../../assets/stroked-circle-cross.svg?react';

import Snackbar from '../../../snackbar';
import Button from '../../../button';
import Select from '../../../input-controls/select';
import Input from '../../../input-controls/input';

import { RECAPTCHA_SITE_KEY } from '../../../../config';

import {
    sendReport,
    onEnterEmail,
    onEnterSubject,
    onEnterMessage,
    onSetReCaptcha,
    onSelectFile,
    clearForm,
} from '../../../../actions/report';
import { hideReport, hideModal } from '../../../../actions/modal';
import { useWindowSize } from '../../../../lib/hooks';
import Spacer from '../../../spacer';

const ReCAPTCHAContainer = styled.div`
    margin: 16px 16px 0 16px;

    @media (max-width: 767px) {
        margin: 32px 16px;
    }
`;

const ButtonsContainer = styled.div`
    display: flex;
    margin-top: 40px;
    padding: 0 16px;

    & > button {
        margin-right: 16px;
    }

    @media (max-width: 767px) {
        flex-direction: column;

        & > button {
            width: 100%;
            margin-right: 0;
        }

        & > button:nth-child(2) {
            margin-top: 16px;
        }
    }

    @media (max-width: 1151px) {
        padding: 0;
    }
`;

const MessageContainer = styled.div`
    padding: 16px 16px 0 16px;

    @media (max-width: 1151px) {
        padding: 0;
    }
`;

export const InvisibleInutFile = styled.input`
    display: none;
`;

const DeleteFileButton = styled.button`
    display: flex;
    align-items: center;
    background-color: transparent;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    height: 32px;
    justify-content: center;
    min-width: 32px;
    outline: none;
    width: 32px;

    &:hover:not([disabled]),
    &:hover:not([disabled]) > svg {
        background-color: var(--blue50);
        fill: var(--blue700);
    }

    & > svg {
        width: 18px;
        height: 18px;
        min-width: 18px;
        min-height: 18px;
        fill: var(--blue500);
    }

    &[disabled] {
        cursor: initial;
        pointer-events: none;
    }

    &[disabled] > svg {
        fill: var(--gray700);
    }
`;

const AttachmentContainer = styled.div`
    margin: 0 16px 0 16px;

    @media (max-width: 767px) {
        flex-direction: column;

        & > button {
            width: 100%;
            margin-right: 0;
        }

        & > button:nth-child(2) {
            margin-top: 16px;
        }
    }

    @media (max-width: 1151px) {
        margin: 0;
    }
`;

const AttachmentName = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    & > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const HelperContainer = styled.div`
    align-items: center;
    display: flex;
    margin: 8px 0 8px 0;
`;

export const Helper = styled.div`
    color: var(--color-error-default);
    display: flex;
    font-size: 14px;
    margin-top: 0px;
    height: 16px;
`;

export const IconContainer = styled.div`
    height: 16px;
    right: 6px;
    width: 16px;

    margin-right: 8px;
`;

export const ErrorIcon = styled(ErrorIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['text-error-red-main']};
    `}
`;

const getMessageFromStatus = (status) => {
    if (status === 'error') {
        return 'Non siamo riusciti a inviare il messaggio';
    }
    if (status === 'waiting') {
        return 'Invio in corso...';
    }
    return 'Messaggio inviato con successo.';
};

export const getSnackbarStatus = (httpStatus) => {
    if (httpStatus.error) {
        return 'error';
    }

    if (httpStatus.fetching) {
        return 'waiting';
    }

    if (httpStatus.loaded) {
        return 'success';
    }

    return null;
};

const showSnackbarOrMessage = (snackbarStatus, snackbarMessage) => {
    if (snackbarStatus) {
        return <Snackbar type={snackbarStatus} message={snackbarMessage} />;
    }
    return (
        <MessageContainer>
            <span className="body-3">
                {'Invieremo la tua segnalazione alla '}
                <span className="body-3-sb">{'Redazione Zanichelli'}</span>
                {', che ti risponderà appena possibile.'}
            </span>
        </MessageContainer>
    );
};

export const FormFields = ({
    currentIndex,
    email,
    exercises,
    homework,
    subject,
    message,
    file,
    reCaptchaToken,
    httpStatus,
    sendReport,
    hideReport,
    hideModal,
    onEnterEmail,
    onEnterSubject,
    onEnterMessage,
    onSetReCaptcha,
    onSelectFile,
    payload,
    clearForm,
    auth,
    isLoggedIn,
}) => {
    const [showSubmitButtons, setShowSubmitButtons] = useState(!!reCaptchaToken || isLoggedIn);
    const [showErrorFileSize, setShowErrorFileSize] = useState(false);
    const [selectTouched, setSelectTouched] = useState(false);
    const { innerWidth, innerHeight } = useWindowSize();
    const refInputFile = useRef();
    const currentExercise = exercises[currentIndex] || {};

    useEffect(() => {
        setShowSubmitButtons(!!reCaptchaToken || isLoggedIn);
    }, [reCaptchaToken, isLoggedIn]);

    const disabled = httpStatus.fetching || httpStatus.loaded;
    const snackbarStatus = getSnackbarStatus(httpStatus);
    const snackbarMessage = getMessageFromStatus(snackbarStatus);

    const onReCaptchChange = (value) => {
        onSetReCaptcha(value);
        setShowSubmitButtons(true);
    };

    const topics = [
        { label: 'Problema tecnico', value: 'problema-tecnico' },
        { label: 'Errore nel testo', value: 'refuso' },
        { label: 'Suggerimento', value: 'suggerimento' },
        { label: 'Altro', value: 'altro' },
    ];

    const onClose = () => {
        hideReport();
        hideModal();
    };

    const onClearForm = () => {
        setSelectTouched(false);
        setShowSubmitButtons(isLoggedIn);
        clearForm();
    };

    const initialEmail = () => {
        if (email !== null) {
            return email;
        }

        if (auth.loginId) {
            return auth.loginId;
        }

        return '';
    };

    const role = auth.isLoggedIn ? auth.role : 'Non registrato';
    return (
        <>
            {showSnackbarOrMessage(snackbarStatus, snackbarMessage)}
            <Formik
                enableReinitialize={true}
                validateOnMount={true}
                initialValues={{
                    email: initialEmail(),
                    topic: subject,
                    message: message,
                }}
                validationSchema={ReportSchema}
                onSubmit={(values) => {
                    const customMessage = `${values.message}\nrole: ${role}\nbookId: ${homework.bookId}\ncourseTitle: ${
                        homework.courseTitle
                    }\nexerciseId:${currentExercise.originalId}\nhomeworkChapter: ${homework.publicTitle}\nsubjects: ${JSON.stringify(
                        homework.subjects,
                    )}\nauthors: ${JSON.stringify(homework.authors)}\nversion: ${homework.version}
                    \nBrowser Resolution: ${innerWidth}x${innerHeight}
                    \nScreen Pixel Ratio: ${window.devicePixelRatio}
                    \nFull Screen: ${window.fullScreen}
                    \nScreen: ${JSON.stringify(window.screen)}
                    \nPayload: ${payload}`;

                    sendReport(values.email, values.topic, customMessage, reCaptchaToken, file, isLoggedIn);
                }}
            >
                {({ touched, errors, values, handleChange, ...props }) => (
                    <Form>
                        <ReportFieldContainer>
                            <Input
                                name="email"
                                placeholder="Inserisci la tua email"
                                label="Email"
                                validation={(values.email && errors.email) || (!values.email && touched.email) ? 'error' : 'default'}
                                helper={(values.email && errors.email) || (!values.email && touched.email) ? errors.email : null}
                                disabled={disabled}
                                onChange={(e) => {
                                    handleChange(e);
                                    onEnterEmail(e.target.value);
                                }}
                                {...props}
                            />
                        </ReportFieldContainer>

                        <ReportFieldContainer>
                            <Select
                                name="topic"
                                disabled={disabled}
                                label="Argomento"
                                placeholder="Seleziona un argomento"
                                styleType={selectTouched && errors.topic ? 'error' : 'default'}
                                helper={selectTouched && errors.topic ? errors.topic : null}
                                options={topics}
                                value={topics.find((t) => t.value === values.topic) || ''}
                                onChange={({ value }) => onEnterSubject(value)}
                                onTouched={() => setSelectTouched(true)}
                                {...props}
                            />
                        </ReportFieldContainer>

                        <ReportFieldContainer>
                            <Input
                                name="message"
                                textarea={true}
                                disabled={disabled}
                                label="Messaggio"
                                placeholder="Inserisci il tuo messaggio"
                                validation={
                                    (values.message && errors.message) || (!values.message && touched.message) ? 'error' : 'default'
                                }
                                helper={(values.message && errors.message) || (!values.message && touched.message) ? errors.message : null}
                                onChange={(e) => {
                                    handleChange(e);
                                    onEnterMessage(e.target.value);
                                }}
                                {...props}
                            />
                        </ReportFieldContainer>

                        <InvisibleInutFile
                            ref={refInputFile}
                            type="file"
                            name="file"
                            accept=".jpg , .png , .pdf"
                            onChange={(e) => {
                                if (!e.target.files || !e.target.files[0]) {
                                    return;
                                }

                                const f = e.target.files[0];
                                const maxSize = 5 * 1024 * 1024;
                                if (f.size > maxSize) {
                                    setShowErrorFileSize(true);
                                    return;
                                }

                                setShowErrorFileSize(false);
                                onSelectFile(e.target.files[0]);
                            }}
                        />

                        {file ? (
                            <ReportFieldContainer>
                                <span className="body-5-sb">{'FILE ALLEGATO'}</span>
                                <Spacer size="small" />
                                <AttachmentName>
                                    <span>{file.name}</span>
                                    <div>
                                        <DeleteFileButton onClick={() => onSelectFile(null)} disabled={disabled}>
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000">
                                                <path d="M258 908H742V244H258ZM330 92V20H670V92ZM366 364H437V788H366ZM563 364H634V788H563ZM70 244V172H930V244H820V894C820 948 788 980 734 980H266C210 980 180 948 180 894V244Z" />
                                            </svg>
                                        </DeleteFileButton>
                                    </div>
                                </AttachmentName>
                            </ReportFieldContainer>
                        ) : (
                            <AttachmentContainer>
                                <Button
                                    type="secondary"
                                    size="small"
                                    disabled={disabled}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        refInputFile.current.click();
                                    }}
                                >
                                    {'ALLEGA FILE'}
                                </Button>

                                {showErrorFileSize ? (
                                    <HelperContainer>
                                        <IconContainer>
                                            <ErrorIcon />
                                        </IconContainer>
                                        <Helper>{'File non caricato (max 5Mb)'}</Helper>
                                    </HelperContainer>
                                ) : (
                                    <>
                                        <Spacer size="small" />
                                        <span className="body-4">{'Carica 1 file JPG, PNG o PDF (max 5Mb)'}</span>
                                    </>
                                )}
                            </AttachmentContainer>
                        )}

                        {!showSubmitButtons && (
                            <ReCAPTCHAContainer>
                                <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={onReCaptchChange} size="normal" hl="it" />
                            </ReCAPTCHAContainer>
                        )}
                        {showSubmitButtons && (
                            <ButtonsContainer>
                                {httpStatus.loaded ? (
                                    <>
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onClose();
                                                onClearForm();
                                            }}
                                        >
                                            {'Chiudi'}
                                        </Button>
                                        <Button
                                            type="tertiary"
                                            size="small"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onClearForm();
                                            }}
                                        >
                                            {'Nuovo messaggio'}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            type="primary"
                                            size="small"
                                            disabled={
                                                disabled ||
                                                errors.email ||
                                                errors.topic ||
                                                errors.message ||
                                                !values.email ||
                                                !values.topic ||
                                                !values.message
                                            }
                                        >
                                            {httpStatus.error ? 'Riprova' : 'Invia'}
                                        </Button>
                                        <Button
                                            type="tertiary"
                                            size="small"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onClose();
                                                onClearForm();
                                            }}
                                            disabled={disabled}
                                        >
                                            {'Annulla'}
                                        </Button>
                                    </>
                                )}
                            </ButtonsContainer>
                        )}
                    </Form>
                )}
            </Formik>
        </>
    );
};

FormFields.propTypes = {
    auth: PropTypes.object,
    clearForm: PropTypes.func,
    currentIndex: PropTypes.number,
    email: PropTypes.string,
    exercises: PropTypes.array,
    file: PropTypes.object,
    hideReport: PropTypes.func,
    hideModal: PropTypes.func,
    homework: PropTypes.object,
    httpStatus: PropTypes.object,
    isLoggedIn: PropTypes.bool,
    message: PropTypes.string,
    onEnterEmail: PropTypes.func,
    onEnterSubject: PropTypes.func,
    onEnterMessage: PropTypes.func,
    onSetReCaptcha: PropTypes.func,
    reCaptchaToken: PropTypes.string,
    sendReport: PropTypes.func,
    status: PropTypes.string,
    subject: PropTypes.string,
    payload: PropTypes.string,
    onSelectFile: PropTypes.func,
};

const mapStateToProps = (state) => {
    return {
        payload: state.application.encodedPayload,
        currentIndex: state.status.currentExerciseIndex,
        email: state.report.email,
        exercises: state.homework.exercises,
        subject: state.report.subject,
        message: state.report.message,
        file: state.report.file,
        reCaptchaToken: state.report.reCaptchaToken,
        httpStatus: state.report.status,
        homework: state.homework,
        auth: state.auth,
        isLoggedIn: state.auth.isLoggedIn && !!!state.application.googleCourseWorkId,
    };
};

const actionCreators = {
    sendReport: sendReport,
    hideReport: hideReport,
    hideModal: hideModal,
    onEnterEmail: onEnterEmail,
    onEnterSubject: onEnterSubject,
    onEnterMessage: onEnterMessage,
    onSetReCaptcha: onSetReCaptcha,
    clearForm: clearForm,
    onSelectFile: onSelectFile,
};

export default connect(mapStateToProps, actionCreators)(FormFields);
